import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import KPICard from "@/components/KPICard";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { relativeTime } from "@/lib/utils";
import {
  Briefcase, Trophy, XCircle, ChatCircleText, ArrowRight, Sparkle, Phone, Envelope,
} from "@phosphor-icons/react";
import { motion } from "framer-motion";
import FilterBar, { emptyFilters, filtersToParams } from "@/components/FilterBar";
import SyncHealthTile from "@/components/SyncHealthTile";
import MismergeAuditTile from "@/components/MismergeAuditTile";
import { useAuth } from "@/lib/auth";

export default function Dashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(emptyFilters());
  const [options, setOptions] = useState(null);

  const load = async (f) => {
    setLoading(true);
    try {
      const params = filtersToParams(f);
      const [a, l] = await Promise.all([
        api.get("/analytics/summary", { params }),
        api.get("/leads", { params: { ...params, sort_by: "received_at", order: "desc", limit: 6 } }),
      ]);
      setStats(a.data);
      setRecent(l.data);
    } catch (e) {
      console.error("Dashboard data load failed:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/reports/options").then((r) => setOptions(r.data)).catch(() => {});
    load(emptyFilters());
  }, []);

  const onApply = (next) => {
    setFilters(next);
    load(next);
  };

  return (
    <div data-testid="dashboard-page" className="space-y-10">
      {/* Header */}
      <div>
        <div className="label-tracking">Overview</div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight leading-none mt-2 text-slate-900">
          Lead Operations.
        </h1>
        <p className="text-sm text-slate-500 mt-3 max-w-2xl leading-relaxed">
          Real-time view of every enquiry arriving at <span className="font-mono text-slate-900">Leads@barnardinc.co.za</span>, intelligently parsed and routed.
        </p>
      </div>

      <FilterBar value={filters} onApply={onApply} options={options} testId="dashboard-filters" />

      {isAdmin && <SyncHealthTile isAdmin={isAdmin} />}
      {isAdmin && <MismergeAuditTile isAdmin={isAdmin} />}

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <KPICard
          testId="kpi-total"
          label="Total Leads"
          value={loading ? "…" : stats?.total ?? 0}
          icon={Briefcase}
          index={0}
        />
        <KPICard
          testId="kpi-new"
          label="Awaiting Action"
          value={loading ? "…" : stats?.by_status?.new ?? 0}
          accent="text-[#002FA7]"
          icon={Sparkle}
          index={1}
          sub={`${stats?.by_status?.contacted ?? 0} contacted · ${stats?.by_status?.qualified ?? 0} qualified`}
        />
        <KPICard
          testId="kpi-won"
          label="Won"
          value={loading ? "…" : stats?.by_status?.won ?? 0}
          accent="text-emerald-700"
          icon={Trophy}
          index={2}
          sub={`Conversion ${stats?.conversion_rate ?? 0}%`}
        />
        <KPICard
          testId="kpi-lost"
          label="Lost"
          value={loading ? "…" : stats?.by_status?.lost ?? 0}
          accent="text-rose-700"
          icon={XCircle}
          index={3}
          sub={stats?.avg_days_to_close != null ? `Avg close ${stats.avg_days_to_close}d` : "—"}
        />
      </div>

      {/* Recent leads */}
      <section>
        <div className="flex items-end justify-between mb-5">
          <div>
            <div className="label-tracking">Recent</div>
            <h2 className="font-display text-2xl sm:text-3xl tracking-tight leading-none mt-1 text-slate-900">
              Latest enquiries
            </h2>
          </div>
          <Link to="/leads">
            <Button
              data-testid="view-all-leads-btn"
              variant="ghost"
              className="text-slate-700 hover:bg-slate-900 hover:text-white rounded-sm gap-2"
            >
              View all <ArrowRight size={14} />
            </Button>
          </Link>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          {loading ? (
            <div className="p-10 text-center text-slate-500 text-sm">Loading…</div>
          ) : recent.length === 0 ? (
            <div className="p-10 text-center text-slate-500 text-sm" data-testid="recent-leads-empty">
              No leads yet. Upload a .msg file or configure Microsoft sync in Settings.
            </div>
          ) : (
            <ul className="divide-y divide-slate-200">
              {recent.map((lead, i) => (
                <motion.li
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: i * 0.04 }}
                  key={lead.id}
                  data-testid={`recent-lead-${i}`}
                >
                  <Link
                    to={`/leads/${lead.id}`}
                    className="block p-4 sm:p-5 hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="flex items-center gap-3 mb-1">
                          <span className="font-display text-lg sm:text-xl text-slate-900 truncate">
                            {lead.name || "Unknown contact"}
                          </span>
                          <StatusBadge status={lead.status} />
                        </div>
                        <div className="text-sm text-slate-600 line-clamp-1">
                          {lead.summary || lead.email_subject}
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-slate-500">
                          {lead.source && <span className="font-mono">{lead.source}</span>}
                          {lead.phone && (
                            <span className="inline-flex items-center gap-1">
                              <Phone size={12} /> {lead.phone}
                            </span>
                          )}
                          {lead.email && (
                            <span className="inline-flex items-center gap-1">
                              <Envelope size={12} /> {lead.email}
                            </span>
                          )}
                          {lead.thread?.length > 1 && (
                            <span className="inline-flex items-center gap-1 text-amber-700">
                              <ChatCircleText size={12} /> {lead.thread.length - 1} {lead.thread.length - 1 === 1 ? "reply" : "replies"}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-xs text-slate-400 shrink-0 hidden sm:block">
                        {relativeTime(lead.received_at)}
                      </div>
                    </div>
                  </Link>
                </motion.li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}
